((L, U) => {
	'use strict';

	/**
	 * Get the selected options from a select
	 *
	 * @private
	 * @param {HTMLElement} select
	 * @returns {Array} selected
	 */
	const _getSelected = select => {
		const arr = [];

		Array.from(select.options).map(el => {
			if (el.selected) {
				arr.push(el.value);
			}
		});

		return arr;
	};

	/**
	 * Get class value of element
	 *
	 * @private
	 * @param {HTMLElement} el
	 * @returns {String}
	 */
	const _getClass = el => {
		return el instanceof SVGElement ?
			el.getAttribute('class') :
			el.className;
	};

	/**
	 * Set class value of element
	 *
	 * @private
	 * @param {HTMLElement} el
	 * @param {String} className
	 */
	const _setClass = (el, className) => {
		el instanceof SVGElement ?
			el.setAttribute('class', className) :
			el.className = className;
	};

	/**
	 * Return either direct previous or next sibling
	 *
	 * @private
	 * @param {$|HTMLElement|String} target
	 * @param {Number} dir
	 * @param {String} [filter]
	 * @param {Object} [options]
	 * @returns {HTMLElement}
	 */
	const _getSibling = (target, dir, filter, options) => {
		let match;

		L.$each(target, el => {
			const index = L.$index(el) + dir;

			L.$children(L.$parent(el)).forEach((el, i) => {
				if (i === index && (
					! filter || filter && L.$is(el, filter, options))
				) {
					match = el;
				}
			});
		});

		return match;
	};

	L.fn.extend({
		/**
		 * Add classes to each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {Array|String} value
		 */
		$addClass(target, value) {
			L.$each(target, el => {
				const cn = _getClass(el);
				const names = cn.split(' ');
				const upd = (Array.isArray(value) ? value : value.split(' '))
					.filter(val => ! names.includes(val));

				upd.unshift(cn);

				_setClass(el, upd.join(' '));
			});
		},

		/**
		 * Insert selection or markup after each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {$|HTMLElement|String} source
		 * @param {Boolean} [remove=false]
		 */
		$after(target, source, remove) {
			L.$each(target, (el, i) => {
				if (typeof source === 'string') {
					source = L.$parseHTML(source);
				}

				L.$each(source, cel => {
					if (i > 0) {
						cel = L.$clone(cel)[0];
					}

					el.parentNode.insertBefore(cel, el.nextSibling);
				}, {
					reverse: true
				});

				if (remove) {
					L.$remove(el);
				}
			});
		},

		/**
		 * Append selection or markup after each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {$|HTMLElement|String} source
		 */
		$append(target, source) {
			L.$each(target, el => {
				if (typeof source === 'string') {
					source = L.$parseHTML(source);
				}

				L.$each(source, cel => {
					el.appendChild(cel);
				});
			});
		},

		/**
		 * Get attribute of first matching selection or set attribute
		 * of each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param a
		 * @param b
		 * @returns {String|Undefined}
		 */
		$attr(target, a, b) {
			if (b === U) {
				return L.$(target)[0].getAttribute(a);
			}

			L.$each(target, el => {
				el.setAttribute(a, b);
			});
		},

		/**
		 * Insert selection or markup before each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {$|HTMLElement|String} source
		 * @param {Boolean} [remove=false]
		 */
		$before(target, source, remove) {
			L.$each(target, (el, i) => {
				if (typeof source === 'string') {
					source = L.$parseHTML(source);
				}

				L.$each(source, cel => {
					if (i > 0) {
						cel = L.$clone(cel)[0];
					}

					el.parentNode.insertBefore(cel, el);
				}, {
					reverse: true
				});

				if (remove) {
					L.$remove(el);
				}
			});
		},

		/**
		 * Get unique direct children of each matching selection
		 *
		 * @param {$|HTMLElement|String} parent
		 * @param {String} [filter]
		 * @returns {Array}
		 */
		$children(parent, filter) {
			let arr = [];

			L.$each(parent, el => {
				const children = Array.from(el.children);

				arr = arr.concat(
					filter ?
						L.$filter(children, filter) :
						children
				);
			});

			return L.$unique(arr);
		},

		/**
		 * Clone each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @returns {Array}
		 */
		$clone(target) {
			return L.$map(target, el => el.cloneNode(true));
		},

		/**
		 * Get unique closest ancestors of each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {String} filter
		 * @param {$|HTMLElement|String} [context]
		 * @returns {HTMLElement}
		 */
		$closest(target, filter, context) {
			return L.$unique(L.$map(target, el => {
				if (L.$is(el, filter)) {
					return el;
				}

				while (el !== null) {
					el = el.parentNode;

					if (el === L._html) {
						return false;
					}

					if (L.$is(el, filter)) {
						return el;
					}
				}
			}, {
				context: context
			}));
		},

		/**
		 * Determine if any matching parent contains descendant selection
		 *
		 * @param {$|HTMLElement|String} parent
		 * @param {$|HTMLElement|String} descendant
		 * @returns {Boolean}
		 */
		$contains(parent, descendant) {
			let b = false;

			L.$each(parent, el => {
				if (L.$(descendant, el).length) {
					b = true;

					return false;
				}
			});

			return b;
		},

		/**
		 * Get CSS value of first matching selection or set value
		 * of each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {String} a
		 * @param {String} [b]
		 * @returns {String|Undefined}
		 */
		$css(target, a, b) {
			if (b === U) {
				return getComputedStyle(L.$(target)[0], null)[a];
			}

			L.$each(target, el => {
				el.style[a] = b;
			});
		},

		/**
		 * Get data of first matching selection or set data
		 * of each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param a
		 * @param [b]
		 * @returns {Object|String|Undefined}
		 */
		$data(target, a, b) {
			if (L.$isObject(b)) {
				b = JSON.stringify(b);
			}

			a = 'data-' + a.replace(/[A-Z]/g, match => '-' + match[0].toLowerCase());

			return L._castString(L.$attr(target, a, b));
		},

		/**
		 * Remove child nodes from each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 */
		$empty(target) {
			L.$each(target, el => {
				if ('replaceChildren' in el) {
					el.replaceChildren();
				} else {
					while (el.firstChild) {
						el.removeChild(el.firstChild);
					}
				}
			});
		},

		/**
		 * Get indexed node of matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {Number} index
		 * @param {$|HTMLElement|String} [context=document]
		 * @returns {HTMLElement}
		 */
		$eq(target, index, context) {
			const el = L.$(target, context);

			return el[
				index < 0 ?
					el.length + index : index
			];
		},

		/**
		 * Return a filtered subset of elements from a matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {String} filter
		 * @param {Object} [options]
		 * @returns {Array} elements
		 */
		$filter(target, filter, options) {
			return L.$map(target, el => L.$is(el, filter, options) ? el : false);
		},

		/**
		 * Get unique filtered descendants from each matching selection
		 *
		 * @param {$|HTMLElement|String} parent
		 * @param {String} filter
		 * @returns {Array} elements
		 */
		$find(parent, filter) {
			let arr = [];

			L.$each(parent, el => {
				arr = arr.concat(L.$(filter, el));
			});

			return L.$unique(arr);
		},

		/**
		 * Get the first element of a matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {$|HTMLElement|String} [context=document]
		 * @returns {HTMLElement}
		 */
		$first(target, context) {
			return L.$(target, context)[0];
		},

		/**
		 * Place focus on input element
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {Boolean} [end=false] - place cursor at the end of the content
		 */
		$focus(target, end) {
			const el = L.$(target)[0];

			el.focus();

			if (end) {
				try {
					const length = el.value.length * 2;

					el.setSelectionRange(length, length);
				} catch (e) {
					//
				}
			}
		},

		/**
		 * Remove focus from an element
		 *
		 * @param {$|HTMLElement|String} target
		 */
		$blur(target) {
			L.$(target)[0].blur();
		},

		/**
		 * Determine if the matching selection has a class
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {String} className
		 * @returns {Boolean}
		 */
		$hasClass(target, className) {
			return L.$(target).some(el => new RegExp('(^| )' + className + '($| )', 'gim')
				.test(_getClass(el))
			);
		},

		/**
		 * Get or set the height of each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {Boolean|Number|String} value
		 * @returns {Number}
		 */
		$height(target, value) {
			if (value === U || value === true) {
				const el = L.$(target)[0];

				let height;

				if (el === L._win) {
					return el.innerHeight;
				}

				if (el === L._doc) {
					return el.documentElement.scrollHeight;
				}

				height = el.offsetHeight;

				if (value === true) {
					const style = getComputedStyle(el);

					height += parseFloat(style.marginTop) +
						parseFloat(style.marginBottom);
				}

				return height;
			}

			L.$each(target, el => {
				if (Number.isFinite(value)) {
					value += 'px';
				}

				L.$css(el, 'height', value);
			});
		},

		/**
		 * Hide each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 */
		$hide(target) {
			L.$prop(target, 'hidden', true);
		},

		/**
		 * Get inner HTML of first selection or set each matching selection HTML
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {String} value
		 * @returns {String|Undefined}
		 */
		$html(target, value) {
			if (value === U) {
				return L.$(target)[0].innerHTML;
			}

			L.$each(target, el => {
				if (value !== false && value !== U) {
					if (el.nodeName === 'SELECT' && ! L._win.atob) {
						el.outerHTML = el.outerHTML.replace(
							el.innerHTML + '</s', value + '</s'
						);
					} else {
						el.innerHTML = value;
					}
				}
			});
		},

		/**
		 * Get the zero-based index of a matching selection relative
		 * to it's siblings
		 *
		 * @param {$|HTMLElement|String} target
		 * @returns {Number}
		 */
		$index(target) {
			const el = L.$(target)[0];

			let i = 0;
			let children;

			if (! el) {
				return -1;
			}

			children = Array.from(el.parentNode.children);

			for (; i < children.length; i++) {
				if (children[i] === el) {
					return i;
				}
			}
		},

		/**
		 * Insert each matching source selection element after
		 * each matching target selection
		 *
		 * @param {$|HTMLElement|String} source
		 * @param {$|HTMLElement|String} target
		 */
		$insertAfter(source, target) {
			L.$each(target, (el, i) => {
				L.$each(source, cel => {
					if (i > 0) {
						cel = L.$clone(cel)[0];
					}

					el.parentNode.insertBefore(cel, el.nextSibling);
				});
			});
		},

		/**
		 * Insert each matching source selection element before
		 * each matching target selection
		 *
		 * @param {$|HTMLElement|String} source
		 * @param {$|HTMLElement|String} target
		 */
		$insertBefore(source, target) {
			L.$each(target, el => {
				L.$each(source, cel => {
					el.parentNode.insertBefore(cel, el);
				});
			});
		},

		/**
		 * Determine if at least one matching selection matches
		 * a specified criteria
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {Array|Function|String} filter
		 * @returns {Boolean}
		 */
		$is(target, filter) {
			return L.$map(target, (el, i) => {
				if (typeof filter === 'string') {
					filter = filter.replace(/\$/g, '.\\$');
				}

				if (L.$isObject(filter)) {
					for (const key in filter) {
						if (filter[key] === el) {
							return true;
						}
					}

					return false;
				}

				if (Array.isArray(filter)) {
					return filter.includes(el);
				}

				if (typeof filter === 'function') {
					return L.$exec(filter, {
						args: [i, el],
						scope: el
					});
				}

				return (el.matches || el.msMatchesSelector).call(el, filter);
			}).length > 0;
		},

		/**
		 * Get the last element of a matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {$|HTMLElement|String} [context=document]
		 * @returns {HTMLElement}
		 */
		$last(target, context) {
			return L.$eq(target, -1, context);
		},

		/**
		 * Get the unique next sibling of each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {String} filter
		 * @param {Object} [options]
		 * @returns {Array} elements
		 */
		$next(target, filter, options) {
			return L.$unique(L.$map(target, el => _getSibling(el, 1, filter, options)));
		},

		/**
		 * Get the position of a matching selection relative to the document
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {Number} value
		 * @returns {{top: number, left: number}}
		 */
		$offset(target, value) {
			if (value) {
				L.$each(target, el => {
					if (Number.isFinite(value.top)) {
						value.top = value.top + 'px';
					}

					if (Number.isFinite(value.left)) {
						value.left = value.left + 'px';
					}

					L.$css(el, value);
				});
			} else {
				const rect = L.$(target)[0].getBoundingClientRect();

				return {
					top: rect.top + L._win.pageYOffset,
					left: rect.left + L._win.pageXOffset
				};
			}
		},

		/**
		 * Get unique parent from each matching selection
		 *
		 * @param {$|HTMLElement|String} child
		 * @param {String} filter
		 * @returns {Array} elements
		 */
		$parent(child, filter) {
			return L.$unique(L.$map(child, el => {
				const parent = el.parentNode;

				return ! filter || L.$is(parent, filter) ?
					parent : false;
			}));
		},

		/**
		 * Prepend selection or markup before each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {$|HTMLElement|String} source
		 */
		$prepend(target, source) {
			L.$each(target, el => {
				if (typeof source === 'string') {
					source = L.$parseHTML(source);
				}

				L.$each(source, cel => {
					el.insertBefore(cel, el.firstChild);
				});
			});
		},

		/**
		 * Get the unique previous sibling of each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {String} filter
		 * @param {Object} [options]
		 * @returns {Array} elements
		 */
		$prev(target, filter, options) {
			return L.$unique(L.$map(target, el => _getSibling(el, -1, filter, options)));
		},

		/**
		 * Get property of first matching selection or set property of
		 * each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param a
		 * @param b
		 * @returns {*}
		 */
		$prop(target, a, b) {
			if (b === U) {
				return L.$(target)[0][a];
			}

			L.$each(target, el => {
				el[a] = b;
			});
		},

		/**
		 * Remove each matching selection from the document
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {$|HTMLElement|String} [context=document]
		 */
		$remove(target, context) {
			L.$each(target, el => {
				el.parentNode.removeChild(el);
			}, {
				context: context
			});
		},

		/**
		 * Remove specified attribute of each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {String} name
		 */
		$removeAttr(target, name) {
			L.$each(target, el => {
				name.split(/\s+/).forEach(value => {
					el.removeAttribute(value);
				});
			});
		},

		/**
		 * Remove classes from each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {Array|String} value
		 */
		$removeClass(target, value) {
			L.$each(target, el => {
				value = Array.isArray(value) ?
					value : value.split(' ');

				_setClass(el, _getClass(el).split(' ')
					.filter(val => ! value.includes(val))
					.join(' '));
			});
		},

		/**
		 * Replace each matching selection with selection or markup
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {$|HTMLElement|String} source
		 */
		$replaceWith(target, source) {
			L.$after(target, source, true);
		},

		/**
		 * Get or set the Y scroll position of each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {Number} value
		 * @param {Boolean} [smooth=false]
		 * @returns {Number}
		 */
		$scrollTop(target, value, smooth) {
			if (value === U) {
				const el = target ?
					L.$(target)[0] : L._win;

				return el === L._win ?
					el.pageYOffset :
					el.scrollTop;
			}

			L.$each(target, el => {
				try {
					el.scrollTo({
						top: value,
						behavior: smooth ?
							'smooth' : 'instant'
					});
				} catch (e) {
					el.scrollTop = value;
				}
			});
		},

		/**
		 * Serialize input values from first matching form selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {Boolean} json
		 * @param {Boolean} [nest=true]
		 * @returns {String}
		 */
		$serializeForm(target, json, nest) {
			const el = L.$(target)[0];
			const obj = {};

			let i = 0;

			if (! el || el.nodeName !== 'FORM') {
				return '';
			}

			for (; i < el.elements.length; i++) {
				const child = el.elements[i];
				const type = child.type;

				let name = child.name;

				if (! name || child.disabled || type === 'file' || type === 'reset') {
					continue;
				}

				const arr = name.slice(-2) === '[]';

				let value = null;

				if (arr) {
					name = name.slice(0, -2);
				}

				if (type === 'select-multiple') {
					value = _getSelected(child);
				} else {
					const toggle = type === 'checkbox' ||
						type === 'radio';

					if (
						type !== 'button' &&
						type !== 'submit' && (
							! toggle || (
								toggle &&
								child.checked
							)
						)
					) {
						if (arr || (type === 'checkbox' && obj[name])) {
							value = L.$toArray(obj[name]);

							value.push(child.value);
						} else {
							value = child.value;
						}
					}
				}

				if (value !== null) {
					obj[name] = value;
				}
			}

			if (! json) {
				return L.$serialize(obj);
			}

			if (nest !== false) {
				const nested = {};

				let seg;
				let sub;

				for (const key in obj) {
					seg = key.replace(/]/g, '').split('[');
					sub = nested;

					if (seg.length < 1) {
						continue;
					}

					for (i = 0; i < seg.length - 1; ++i) {
						const node = seg[i];

						sub = sub[node] === undefined ?
							(sub[node] = {}) : sub[node];
					}

					sub[seg[seg.length - 1]] = obj[key];
				}

				return nested;
			}

			return obj;
		},

		/**
		 * Show each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 */
		$show(target) {
			L.$prop(target, 'hidden', false);
		},

		/**
		 * Get unique siblings of each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {String} [filter]
		 * @returns {Array} elements
		 */
		$siblings(target, filter) {
			let arr = [];

			L.$each(target, el => {
				const siblings = Array.from(el.parentNode.children)
					.filter(a => a !== el);

				arr = arr.concat(
					filter ?
						L.$filter(siblings, filter) :
						siblings
				);
			});

			return L.$unique(arr);
		},

		/**
		 * Get inner text of first selection or set each matching selection text
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {String} value
		 * @returns {String}
		 */
		$text(target, value) {
			if (value === U) {
				return L.$map(target, el => el.textContent.trim())
					.join('');
			}

			L.$each(target, el => {
				el.textContent = value;
			});
		},

		/**
		 * Toggle the display of each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {Boolean} [state]
		 */
		$toggle(target, state) {
			L.$each(target, el => {
				state === false || (state === U && ! L.$prop(el, 'hidden')) ?
					L.$hide(el) :
					L.$show(el);
			});
		},

		/**
		 * Toggle adding and removing class(es) from the specified element
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {Array|String} value
		 * @param {Boolean} [state]
		 */
		$toggleClass(target, value, state) {
			L.$each(target, el => {
				(Array.isArray(value) ? value : value.split(' ')).forEach(value => {
					state === false || (state === U && L.$hasClass(el, value)) ?
						L.$removeClass(el, value) :
						L.$addClass(el, value);
				});
			});
		},

		/**
		 * Get value of first matching selection or set match values
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {String} value
		 * @returns {Array|String}
		 */
		$val(target, value) {
			if (value === U) {
				const el = L.$(target)[0];

				if (el.type === 'select-multiple') {
					return _getSelected(el);
				}

				return el.value;
			}

			L.$each(target, el => {
				if (el.type === 'select-multiple') {
					value = L.$toArray(value);

					Array.from(el.options).forEach(a => {
						if (value.includes(a.value)) {
							a.selected = true;
						}
					});
				} else {
					el.value = value;
				}
			});
		},

		/**
		 * Get or set the width of each matching selection
		 *
		 * @param {$|HTMLElement|String} target
		 * @param {Boolean|Number|String} value
		 * @returns {Number}
		 */
		$width(target, value) {
			if (value === U || value === true) {
				const el = L.$(target)[0];

				let width;

				if (el === L._win) {
					return el.innerWidth;
				}

				if (el === L._doc) {
					return el.documentElement.scrollWidth;
				}

				width = el.offsetWidth;

				if (value === true) {
					const style = getComputedStyle(el);

					width += parseFloat(style.marginLeft) +
						parseFloat(style.marginRight);
				}

				return width;
			}

			L.$each(target, el => {
				if (Number.isFinite(value)) {
					value += 'px';
				}

				L.$css(el, 'width', value);
			});
		}
	});
})(Apt, undefined);